import {
  Drawer as DrawerBase,
  DrawerBody as DrawerBodyBase,
  DrawerCloseButton as DrawerCloseButtonBase,
  DrawerContent as DrawerContentBase,
  DrawerHeader as DrawerHeaderBase,
  DrawerOverlay as DrawerOverlayBase,
  useBreakpointValue,
} from '@chakra-ui/react';
import { Fragment, useEffect, useState } from 'react';
import { FiArrowRight, FiLink, FiMessageCircle, FiRefreshCw } from 'react-icons/fi';
import { variaveisDoTema } from '../visual';
import { Aviso, Botao, BotaoDeTexto, Box, Cartao, Esqueleto, Etiqueta, Faixa, Flex, NaturezaDoAlerta, Selo, Variacao } from './base';
import { cabecalho, campo, curto, estadoDoAlerta, numerosDoAlerta, plural, reais } from './formato';
import { Alerta, Corte, Investigacao, NomeDaAnalise, Periodo, PorQueDoNumero } from './tipos';

const Drawer = DrawerBase as React.ComponentType<any>;
const DrawerBody = DrawerBodyBase as React.ComponentType<any>;
const DrawerCloseButton = DrawerCloseButtonBase as React.ComponentType<any>;
const DrawerContent = DrawerContentBase as React.ComponentType<any>;
const DrawerHeader = DrawerHeaderBase as React.ComponentType<any>;
const DrawerOverlay = DrawerOverlayBase as React.ComponentType<any>;

// O que a gaveta mostra: um alerta (e, se for de variação da venda, a
// investigação dele) ou só a investigação de uma análise inteira.
export interface PedidoDeInvestigacao {
  analise: NomeDaAnalise;
  periodo: Periodo;
  colaborador_id?: string;
  vendedor?: string;
}

export interface ConteudoDaGaveta {
  titulo: string;
  alerta?: Alerta;
  investigar?: PedidoDeInvestigacao;
}

function Bloco({ titulo, legenda, children }: { titulo: string; legenda?: string; children: React.ReactNode }) {
  return (
    <Box as="section" mt="20px">
      <Box as="h3" m={0} fontSize="14px" fontWeight={700}>
        {titulo}
      </Box>
      {legenda && (
        <Box fontSize="12px" color="var(--pa-tinta-3)" mt="2px">
          {legenda}
        </Box>
      )}
      <Box mt="8px">{children}</Box>
    </Box>
  );
}

function Lista({ itens }: { itens: React.ReactNode[] }) {
  return (
    <Box as="ul" m={0} pl="18px" fontSize="13px" lineHeight="20px" color="var(--pa-tinta-2)">
      {itens.map((t, i) => (
        <Box as="li" key={i} mb="4px">
          {t}
        </Box>
      ))}
    </Box>
  );
}

// Uma soma que fecha: as partes e o total, com o sinal de cada uma.
function Decomposicao({ partes, total }: { partes: [string, number][]; total: [string, number] }) {
  const sinal = (v: number) => (v > 0 ? '+ ' : v < 0 ? '− ' : '');
  const linha = (rotulo: string, valor: number, forte?: boolean) => (
    <Flex
      key={rotulo}
      justify="space-between"
      sx={{ gap: '12px' }}
      py="6px"
      borderTop={forte ? '1px solid var(--pa-linha)' : undefined}
      fontSize="13px"
      fontWeight={forte ? 700 : 400}
    >
      <Box color={forte ? 'var(--pa-tinta)' : 'var(--pa-tinta-2)'}>{rotulo}</Box>
      <Box
        whiteSpace="nowrap"
        color={valor < 0 ? 'var(--pa-desce)' : valor > 0 ? 'var(--pa-sobe)' : undefined}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {sinal(valor)}
        {reais(Math.abs(valor))}
      </Box>
    </Flex>
  );
  return (
    <Cartao px="14px" py="6px" boxShadow="none">
      {partes.map(([r, v]) => linha(r, v))}
      {linha(total[0], total[1], true)}
    </Cartao>
  );
}

// Os maiores de um corte (vendedor, cliente, produto): diferença e a parte
// de cada um na soma das reduções (ou dos aumentos).
function Maiores({ corte, nome, rotulo }: { corte?: Corte; nome: string; rotulo: string }) {
  if (!corte?.maiores?.length) return null;
  const parte = Object.keys(corte.maiores[0]).find((k) => k.startsWith('participacao_na_soma'));
  return (
    <Bloco
      titulo={rotulo}
      legenda={
        parte
          ? `A parte de cada um é sobre a ${parte.includes('reducoes') ? 'soma das reduções' : 'soma dos aumentos'}, não sobre a diferença líquida.`
          : undefined
      }
    >
      <Cartao boxShadow="none" overflow="hidden">
        {corte.maiores.slice(0, 5).map((l, i) => (
          <Flex
            key={i}
            justify="space-between"
            align="baseline"
            sx={{ gap: '12px' }}
            px="12px"
            py="8px"
            borderTop={i ? '1px solid var(--pa-grade)' : undefined}
            fontSize="13px"
          >
            <Box minW={0} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={String(l[nome])}>
              {String(l[nome])}
            </Box>
            <Flex align="baseline" sx={{ gap: '8px' }} flexShrink={0}>
              <Box color={l.diferenca < 0 ? 'var(--pa-desce)' : 'var(--pa-sobe)'} fontWeight={600}>
                {l.diferenca < 0 ? '− ' : '+ '}
                {reais(Math.abs(l.diferenca))}
              </Box>
              {parte && typeof l[parte] === 'number' && (
                <Box fontSize="12px" color="var(--pa-tinta-3)">
                  {campo(parte, l[parte])}
                </Box>
              )}
            </Flex>
          </Flex>
        ))}
      </Cartao>
      {!!corte.fora_da_lista && (
        <Box fontSize="12px" color="var(--pa-tinta-3)" mt="4px">
          e mais {corte.fora_da_lista} fora da lista
        </Box>
      )}
    </Bloco>
  );
}

function ResultadoDaInvestigacao({ inv }: { inv: Investigacao }) {
  const v = inv.venda;
  const pt = inv.pedidos_e_ticket;
  const cl = inv.clientes;
  const pr = inv.produtos;
  return (
    <Box>
      {inv.periodo && (
        <Box fontSize="14px" lineHeight="20px" fontWeight={500} color="var(--pa-tinta-2)">
          {curto(inv.periodo.atual)}
          {inv.periodo.anterior ? ` contra ${curto(inv.periodo.anterior)}` : ''}
          {inv.vendedor ? ` · só ${inv.vendedor}` : ''}
        </Box>
      )}
      {v && (
        <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" sx={{ gap: '8px' }} mt="10px">
          {(
            [
              ['Período anterior', reais(v.anterior.venda), `${plural(v.anterior.pedidos, 'pedido', 'pedidos')}`],
              ['Período atual', reais(v.atual.venda), `${plural(v.atual.pedidos, 'pedido', 'pedidos')}`],
              ['Diferença', `${v.diferenca < 0 ? '−' : '+'} ${reais(Math.abs(v.diferenca))}`, ''],
            ] as [string, string, string][]
          ).map(([r, valor, legenda]) => (
            <Cartao key={r} px="12px" py="10px" boxShadow="none">
              <Box fontSize="12px" color="var(--pa-tinta-3)">
                {r}
              </Box>
              <Box fontSize="16px" fontWeight={700} whiteSpace="nowrap">
                {valor}
              </Box>
              {legenda && (
                <Box fontSize="12px" color="var(--pa-tinta-3)">
                  {legenda}
                </Box>
              )}
            </Cartao>
          ))}
          <Cartao px="12px" py="10px" boxShadow="none">
            <Box fontSize="12px" color="var(--pa-tinta-3)">
              Variação
            </Box>
            <Box mt="4px">
              <Variacao valor={v.variacao_percentual} />
            </Box>
          </Cartao>
        </Box>
      )}
      {inv.relevancia && (
        <Box mt="12px">
          <Aviso tom={inv.relevante ? 'atencao' : 'info'}>{inv.relevancia}</Aviso>
        </Box>
      )}

      {!!inv.comprovado?.length && (
        <Bloco titulo="Comprovado pelos números" legenda="Contas exatas: as partes somam a diferença inteira.">
          <Lista itens={inv.comprovado} />
        </Bloco>
      )}

      {v && pt && pt.efeito_do_numero_de_pedidos != null && pt.efeito_do_ticket_medio != null && (
        <Bloco titulo="Pedidos × ticket médio" legenda="(pedidos atuais − anteriores) × ticket anterior; (ticket atual − anterior) × pedidos atuais.">
          <Decomposicao
            partes={[
              [`Número de pedidos (${pt.pedidos_anterior} → ${pt.pedidos_atual})`, pt.efeito_do_numero_de_pedidos],
              ['Ticket médio', pt.efeito_do_ticket_medio],
            ]}
            total={['Diferença da venda', v.diferenca]}
          />
        </Bloco>
      )}

      {v && cl?.nao_compraram_neste_periodo && (
        <Bloco titulo="Clientes" legenda="Quem deixou de comprar, quem comprou pela primeira vez e a diferença dos que compraram nos dois.">
          <Decomposicao
            partes={[
              [`Não compraram neste período (${cl.nao_compraram_neste_periodo.clientes})`, -cl.nao_compraram_neste_periodo.venda_anterior],
              [`Sem compra no anterior (${cl.sem_compra_no_periodo_anterior.clientes})`, cl.sem_compra_no_periodo_anterior.venda_atual],
              [`Compraram nos dois (${cl.compraram_nos_dois_periodos.clientes})`, cl.compraram_nos_dois_periodos.diferenca],
            ]}
            total={['Diferença da venda', v.diferenca]}
          />
        </Bloco>
      )}

      {pr && pr.efeito_quantidade != null && pr.efeito_preco != null && pr.diferenca_dos_itens != null && (
        <Bloco titulo="Produtos: quantidade × preço" legenda="Os itens enviados ao ERP; produto que só aparece num dos períodos não separa quantidade e preço.">
          <Decomposicao
            partes={[
              ['Quantidade vendida', pr.efeito_quantidade],
              ['Preço médio', pr.efeito_preco],
              ...(pr.sem_separar_quantidade_e_preco ? ([['Sem separar (produto num período só)', pr.sem_separar_quantidade_e_preco]] as [string, number][]) : []),
            ]}
            total={['Diferença dos itens', pr.diferenca_dos_itens]}
          />
        </Bloco>
      )}

      <Maiores corte={inv.vendedores} nome="vendedor" rotulo="Vendedores que mais pesaram" />
      <Maiores corte={inv.clientes} nome="cliente" rotulo="Clientes que mais pesaram" />
      <Maiores corte={inv.produtos} nome="produto" rotulo="Produtos que mais pesaram" />

      {!!inv.possiveis_explicacoes?.length && (
        <Bloco titulo="Explicações possíveis" legenda="Têm um indício nos dados, mas não estão comprovadas.">
          <Flex direction="column" sx={{ gap: '8px' }}>
            {inv.possiveis_explicacoes.map((e, i) => (
              <Cartao key={i} px="12px" py="10px" boxShadow="none">
                <Flex align="center" sx={{ gap: '6px' }} mb="4px">
                  <Etiqueta>Hipótese</Etiqueta>
                </Flex>
                <Box fontSize="13px" fontWeight={600} lineHeight="19px">
                  {e.explicacao}
                </Box>
                <Box fontSize="12px" color="var(--pa-tinta-2)" mt="4px" lineHeight="18px">
                  <b>Indício:</b> {e.indicio}
                </Box>
                <Box fontSize="12px" color="var(--pa-tinta-2)" mt="2px" lineHeight="18px">
                  <b>Como confirmar:</b> {e.como_confirmar}
                </Box>
              </Cartao>
            ))}
          </Flex>
        </Bloco>
      )}

      {!!inv.sem_dados_para_concluir?.length && (
        <Bloco titulo="O que os números não dizem" legenda="O porquê fora dos dados do sistema não é conclusão.">
          <Lista itens={inv.sem_dados_para_concluir} />
        </Bloco>
      )}

      {!!inv.onde_investigar?.length && (
        <Bloco titulo="Por onde começar">
          <Flex direction="column" sx={{ gap: '8px' }}>
            {inv.onde_investigar.map((a, i) => (
              <Flex key={i} align="flex-start" sx={{ gap: '8px' }} fontSize="13px" lineHeight="19px">
                <Box as="span" mt="3px" color="var(--pa-marca-tinta)" flexShrink={0}>
                  <FiArrowRight size={14} aria-hidden="true" />
                </Box>
                <Box>
                  <b>{a.acao}</b>
                  <Box color="var(--pa-tinta-3)" fontSize="12px">
                    {a.motivo}
                  </Box>
                </Box>
              </Flex>
            ))}
          </Flex>
        </Bloco>
      )}

      {!!inv.limitacoes?.length && (
        <Bloco titulo="Limitações">
          <Lista itens={inv.limitacoes} />
        </Bloco>
      )}
    </Box>
  );
}

const CAMPOS_DO_ERRO = ['motivo_do_erro', 'produto_apontado', 'mensagem_do_erp'];

const TITULO_DA_COMPOSICAO: Record<string, string> = {
  por_motivo_do_erro: 'Por motivo do erro',
  por_tipo_de_pedido: 'Por tipo de pedido',
  por_tempo_em_aberto: 'Há quanto tempo estão em aberto',
};

// Período, contra o quê e o indicador: uma ficha no topo, não uma linha cinza.
function FichaDoAlerta({ alerta }: { alerta: Alerta }) {
  const indicador = alerta.indicador || '';
  const linhas = (
    [
      ['Período', alerta.periodo],
      ['Comparado com', (alerta.comparacao || '').replace(/^contra\s+/i, '')],
      ['Indicador', indicador.charAt(0).toUpperCase() + indicador.slice(1)],
    ] as [string, string][]
  ).filter(([, v]) => v);
  return (
    <Box
      as="dl"
      m={0}
      mt="14px"
      display="grid"
      gridTemplateColumns="max-content minmax(0, 1fr)"
      alignItems="baseline"
      sx={{ gap: '6px 16px' }}
      px="14px"
      py="12px"
      borderRadius="10px"
      bg="var(--pa-superficie-2)"
      border="1px solid var(--pa-grade)"
    >
      {linhas.map(([rotulo, valor]) => (
        <Fragment key={rotulo}>
          <Box as="dt" fontSize="13px" color="var(--pa-tinta-3)">
            {rotulo}
          </Box>
          <Box as="dd" m={0} fontSize="15px" lineHeight="22px" fontWeight={600}>
            {valor}
          </Box>
        </Fragment>
      ))}
    </Box>
  );
}

// Cada número como atual contra anterior, com a variação que veio do servidor;
// o que não tem par fica em cartões menores. O nome que já está no título (o
// vendedor, o cliente) não vira cartão.
function NumerosDoAlerta({ alerta }: { alerta: Alerta }) {
  const { comparados, outros } = numerosDoAlerta(alerta.valores);
  const resto = outros.filter(([, v]) => !(typeof v === 'string' && alerta.titulo.includes(v)));
  if (!comparados.length && !resto.length) return null;
  return (
    <Bloco titulo="Números do alerta">
      {comparados.length > 0 && (
        <Faixa minimo="200px" sx={{ gap: '8px' }}>
          {comparados.map((c) => (
            <Cartao key={c.base} px="14px" py="12px" boxShadow="none">
              <Flex justify="space-between" align="center" wrap="wrap" sx={{ gap: '4px 8px' }}>
                <Box fontSize="13px" fontWeight={500} color="var(--pa-tinta-2)">
                  {c.rotulo}
                </Box>
                {c.variacao !== undefined && <Variacao valor={c.variacao} sufixo={c.sufixo} />}
              </Flex>
              <Box mt="4px" fontSize="20px" lineHeight="28px" fontWeight={700} whiteSpace="nowrap">
                {c.atual}
              </Box>
              <Box mt="2px" fontSize="13px" lineHeight="19px" color="var(--pa-tinta-3)">
                Anterior: <Box as="span" color="var(--pa-tinta-2)">{c.anterior}</Box>
              </Box>
              {c.diferenca !== undefined && (
                <Box fontSize="13px" lineHeight="19px" color="var(--pa-tinta-3)">
                  Diferença:{' '}
                  <Box
                    as="span"
                    fontWeight={600}
                    color={c.diferenca < 0 ? 'var(--pa-desce)' : c.diferenca > 0 ? 'var(--pa-sobe)' : 'var(--pa-tinta-2)'}
                  >
                    {c.diferenca < 0 ? '− ' : c.diferenca > 0 ? '+ ' : ''}
                    {reais(Math.abs(c.diferenca))}
                  </Box>
                </Box>
              )}
            </Cartao>
          ))}
        </Faixa>
      )}
      {resto.length > 0 && (
        <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(150px, 1fr))" sx={{ gap: '8px' }} mt={comparados.length ? '8px' : 0}>
          {resto.map(([chave, valor]) => (
            <Cartao key={chave} px="12px" py="9px" boxShadow="none">
              <Box fontSize="12px" color="var(--pa-tinta-3)">
                {cabecalho(chave)}
              </Box>
              <Box fontSize="15px" fontWeight={600}>
                {campo(chave, valor)}
              </Box>
            </Cartao>
          ))}
        </Box>
      )}
    </Bloco>
  );
}

const PAPEL_DO_NUMERO: Record<PorQueDoNumero['papel'], string> = {
  motivo: 'Motivo do alerta',
  dentro: 'Dentro do limite',
  condicao: 'Mínimo para avaliar',
};

// O porquê de cada número, com o mesmo nome do cartão: quem lê vê qual número
// fez o alerta sair e quais só são o mínimo para a regra avaliar. Sem ele, a
// justificativa numa frase.
function PorQueDoAlerta({ alerta }: { alerta: Alerta }) {
  const lista = alerta.por_que_de_cada_numero || [];
  if (!lista.length)
    return (
      <Box as="p" m={0} fontSize="14px" lineHeight="21px" color="var(--pa-tinta-2)">
        {alerta.justificativa}
      </Box>
    );
  const { comparados } = numerosDoAlerta(alerta.valores);
  const nome = (numero: string) => comparados.find((c) => c.chave === numero)?.rotulo || cabecalho(numero);
  return (
    <Flex direction="column" sx={{ gap: '8px' }}>
      {lista.map((p) => {
        const motivo = p.papel === 'motivo';
        return (
          <Box
            key={p.numero}
            px="14px"
            py="10px"
            borderRadius="10px"
            border="1px solid var(--pa-linha)"
            borderLeft={`3px solid ${motivo ? `var(--pa-${alerta.classe})` : 'var(--pa-grade)'}`}
          >
            <Flex align="center" wrap="wrap" sx={{ gap: '4px 8px' }}>
              <Box fontSize="14px" fontWeight={700}>
                {nome(p.numero)}
              </Box>
              <Box
                as="span"
                px="7px"
                py="1px"
                borderRadius="999px"
                fontSize="12px"
                fontWeight={600}
                lineHeight="17px"
                whiteSpace="nowrap"
                color={motivo ? `var(--pa-${alerta.classe})` : 'var(--pa-tinta-2)'}
                bg={motivo ? `var(--pa-${alerta.classe}-fundo)` : 'var(--pa-superficie-2)'}
              >
                {PAPEL_DO_NUMERO[p.papel] || p.papel}
              </Box>
            </Flex>
            <Box mt="4px" fontSize="14px" lineHeight="21px" color="var(--pa-tinta-2)">
              {p.texto}
            </Box>
          </Box>
        );
      })}
    </Flex>
  );
}

function DetalheDoAlerta({ alerta }: { alerta: Alerta }) {
  const estado = estadoDoAlerta(alerta.acompanhamento);
  const composicoes = (['por_motivo_do_erro', 'por_tipo_de_pedido', 'por_tempo_em_aberto'] as const)
    .map((k) => [k, alerta.valores?.[k]] as const)
    .filter(([, v]) => Array.isArray(v) && v.length) as [string, { nome: string; pedidos: number; valor: number }[]][];
  const itens = alerta.itens || [];
  // O motivo do erro é texto longo: vai num bloco próprio, não na tabela, que corta.
  const comErro = itens.some((item) => item.motivo_do_erro);
  const colunas = itens.length ? Object.keys(itens[0]).filter((c) => !CAMPOS_DO_ERRO.includes(c)) : [];
  return (
    <Box>
      {/* De cima para baixo: quando e o quê, os números, por que é alerta e o que fazer. */}
      <Flex wrap="wrap" align="center" sx={{ gap: '6px' }}>
        <Selo classe={alerta.classe} />
        <NaturezaDoAlerta natureza={alerta.natureza} />
        {estado && <Etiqueta tom={estado.tom}>{estado.rotulo}</Etiqueta>}
      </Flex>
      <FichaDoAlerta alerta={alerta} />

      <NumerosDoAlerta alerta={alerta} />

      <Bloco titulo="Por que é alerta">
        <PorQueDoAlerta alerta={alerta} />
        {estado && (
          <Box fontSize="13px" lineHeight="19px" color="var(--pa-tinta-3)" mt="6px">
            {estado.detalhe}
          </Box>
        )}
        {alerta.relacao && (
          <Flex mt="8px" align="flex-start" sx={{ gap: '8px' }} fontSize="13px" lineHeight="19px" color="var(--pa-tinta-2)">
            <Box as="span" mt="3px" flexShrink={0}>
              <FiLink size={13} aria-hidden="true" />
            </Box>
            <span>{alerta.relacao}</span>
          </Flex>
        )}
      </Bloco>

      <Bloco titulo="O que fazer">
        <Flex
          px="14px"
          py="12px"
          borderRadius="10px"
          bg="var(--pa-superficie-2)"
          border="1px solid var(--pa-grade)"
          borderLeft="3px solid var(--pa-marca-tinta)"
          align="flex-start"
          sx={{ gap: '10px' }}
          fontSize="14px"
          lineHeight="21px"
        >
          <Box as="span" color="var(--pa-marca-tinta)" mt="3px" flexShrink={0}>
            <FiArrowRight size={15} aria-hidden="true" />
          </Box>
          <Box>{alerta.acao}</Box>
        </Flex>
      </Bloco>

      {composicoes.map(([k, partes]) => {
        const maior = Math.max(...partes.map((p) => p.valor), 1);
        return (
          <Bloco
            key={k}
            titulo={TITULO_DA_COMPOSICAO[k] || k}
            legenda={
              k === 'por_tempo_em_aberto'
                ? 'Dias corridos desde a emissão. Pedido em aberto há meses costuma pedir limpeza no ERP, não cobrança do faturamento.'
                : undefined
            }
          >
            <Flex direction="column" sx={{ gap: '8px' }}>
              {partes.map((p) => (
                <Box key={p.nome}>
                  <Flex justify="space-between" fontSize="13px" sx={{ gap: '8px' }}>
                    <span>{p.nome}</span>
                    <Box whiteSpace="nowrap" color="var(--pa-tinta-2)">
                      {reais(p.valor)} · {plural(p.pedidos, 'pedido', 'pedidos')}
                    </Box>
                  </Flex>
                  <Box h="6px" mt="4px" borderRadius="3px" bg="var(--pa-grade)" overflow="hidden">
                    <Box h="100%" w={`${(p.valor / maior) * 100}%`} bg="var(--pa-dado)" borderRadius="3px" />
                  </Box>
                </Box>
              ))}
            </Flex>
          </Bloco>
        );
      })}

      {comErro && (
        <Bloco titulo="Por que o ERP recusou cada pedido" legenda="O retorno do Protheus, o mesmo que aparece na tela do pedido.">
          <Flex direction="column" sx={{ gap: '8px' }}>
            {itens.map((item, i) => (
              <Box key={i} border="1px solid var(--pa-linha)" borderLeft="3px solid var(--pa-critico)" borderRadius="8px" px="12px" py="9px" fontSize="13px" lineHeight="19px">
                <Flex justify="space-between" wrap="wrap" sx={{ gap: '4px 12px' }} fontSize="12px" color="var(--pa-tinta-2)">
                  <span>
                    <b>{String(item.pedido ?? '')}</b>
                    {item.cliente ? ` · ${item.cliente}` : ''}
                    {item.vendedor ? ` · ${item.vendedor}` : ''}
                  </span>
                  <span>
                    {typeof item.valor === 'number' ? reais(item.valor) : ''}
                    {item.emissao ? ` · ${item.emissao}` : ''}
                  </span>
                </Flex>
                <Box mt="4px" fontWeight={600}>
                  {String(item.motivo_do_erro)}
                </Box>
                {!!item.produto_apontado && (
                  <Box mt="2px">
                    <b>Produto apontado:</b> {String(item.produto_apontado)}
                  </Box>
                )}
                {!!item.mensagem_do_erp && (
                  <Box mt="4px" px="8px" py="4px" borderRadius="6px" bg="var(--pa-superficie-2)" fontFamily="mono" fontSize="12px" color="var(--pa-tinta-2)">
                    {String(item.mensagem_do_erp)}
                  </Box>
                )}
              </Box>
            ))}
          </Flex>
        </Bloco>
      )}

      {itens.length > 0 && (
        <Bloco titulo="A lista" legenda={alerta.itens_fora_da_lista ? `Os ${itens.length} primeiros; mais ${alerta.itens_fora_da_lista} fora da lista.` : undefined}>
          {/* A lista vem inteira (centenas de pedidos, nos antigos sem nota): rola por dentro. */}
          <Box border="1px solid var(--pa-linha)" borderRadius="10px" overflowX="auto" overflowY="auto" maxH="60vh">
            <Box minW={`${Math.max(colunas.length * 120, 420)}px`}>
              <Box
                display="grid"
                gridTemplateColumns={`minmax(0, 1.6fr) repeat(${Math.max(colunas.length - 1, 0)}, minmax(0, 1fr))`}
                sx={{ gap: '10px' }}
                px="10px"
                py="6px"
                fontSize="11px"
                fontWeight={600}
                color="var(--pa-tinta-3)"
                bg="var(--pa-superficie-2)"
                position="sticky"
                top={0}
                zIndex={1}
              >
                {colunas.map((c) => (
                  <Box key={c} textAlign={typeof itens[0][c] === 'number' ? 'right' : 'left'}>
                    {cabecalho(c)}
                  </Box>
                ))}
              </Box>
              {itens.map((item, i) => (
                <Box
                  key={i}
                  display="grid"
                  gridTemplateColumns={`minmax(0, 1.6fr) repeat(${Math.max(colunas.length - 1, 0)}, minmax(0, 1fr))`}
                  sx={{ gap: '10px' }}
                  px="10px"
                  py="7px"
                  borderTop="1px solid var(--pa-grade)"
                  fontSize="12px"
                >
                  {colunas.map((c) => (
                    <Box
                      key={c}
                      textAlign={typeof item[c] === 'number' ? 'right' : 'left'}
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                      title={String(item[c] ?? '')}
                    >
                      {campo(c, item[c])}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Bloco>
      )}
    </Box>
  );
}

export function Gaveta({
  conteudo,
  ehClaro,
  onFechar,
  onPerguntar,
  investigar,
}: {
  conteudo: ConteudoDaGaveta | null;
  ehClaro: boolean;
  onFechar: () => void;
  onPerguntar: (texto: string) => void;
  investigar: (p: PedidoDeInvestigacao) => Promise<Investigacao>;
}) {
  const tamanho = useBreakpointValue({ base: 'full', md: 'lg' }) || 'lg';
  const [inv, setInv] = useState<Investigacao | null>(null);
  const [tentativa, setTentativa] = useState(0);
  const pedido = conteudo?.investigar;

  useEffect(() => {
    setInv(null);
    if (!pedido) return;
    let vivo = true;
    investigar(pedido).then((r) => vivo && setInv(r));
    return () => {
      vivo = false;
    };
    // `investigar` guarda o que já buscou: refazer só ao trocar o pedido.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedido?.analise, pedido?.periodo.inicio, pedido?.colaborador_id, tentativa]);

  return (
    <Drawer isOpen={!!conteudo} placement="right" onClose={onFechar} size={tamanho}>
      <DrawerOverlay />
      <DrawerContent style={variaveisDoTema(ehClaro)} bg="var(--pa-superficie)" color="var(--pa-tinta)">
        <DrawerCloseButton />
        <DrawerHeader pr="48px" fontSize="17px" lineHeight="24px" borderBottom="1px solid var(--pa-linha)">
          {conteudo?.titulo}
        </DrawerHeader>
        <DrawerBody py="16px" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {conteudo?.alerta && <DetalheDoAlerta alerta={conteudo.alerta} />}
          {pedido && (
            <Box mt={conteudo?.alerta ? '24px' : 0} pt={conteudo?.alerta ? '16px' : 0} borderTop={conteudo?.alerta ? '1px solid var(--pa-linha)' : undefined}>
              <Box as="h3" m={0} fontSize="15px" fontWeight={700}>
                Onde está a diferença
              </Box>
              {!inv && (
                <Box mt="12px" aria-busy="true" aria-label="Investigando">
                  <Esqueleto h="70px" mb="8px" />
                  <Esqueleto h="160px" />
                </Box>
              )}
              {inv && !inv.disponivel && (
                <Box mt="12px">
                  <Aviso tom="atencao">{inv.mensagem || 'Não foi possível investigar agora.'}</Aviso>
                  <Botao mt="8px" leftIcon={<FiRefreshCw />} onClick={() => setTentativa(tentativa + 1)}>
                    Tentar de novo
                  </Botao>
                </Box>
              )}
              {inv?.disponivel && <Box mt="6px"><ResultadoDaInvestigacao inv={inv} /></Box>}
            </Box>
          )}
          {conteudo?.alerta && (
            <Flex mt="24px" pt="12px" borderTop="1px solid var(--pa-linha)" justify="flex-end">
              <BotaoDeTexto
                leftIcon={<FiMessageCircle aria-hidden="true" />}
                onClick={() => {
                  onPerguntar(conteudo.alerta!.pergunta);
                  onFechar();
                }}
              >
                Perguntar ao assistente
              </BotaoDeTexto>
            </Flex>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
